/**
 * Vehicles (ROUND4 §1): lorries, staff cars, the train, a launcher under way. They are machines like the
 * minitank - armour a carbine and a grenade cannot touch, no state machine, no detection meter, permanently
 * `state: 'down'` so no infantry code ever hears through them, rallies to them or takes prisoners with them.
 *
 * What they do instead of fighting is drive. A vehicle follows `SpawnDef.route` waypoint by waypoint at its
 * own speed, on a cost-free line: trenches, minefields and its own garrison's wire mean nothing to it. At the
 * last waypoint, with `loop` false, it LEAVES the map (vehicleCrew.ts `sweepDeparted`) rather than standing
 * there for ever. Anything solid enough to push it off that line stops it dead, and men who stay in front of
 * it go under it - the ranger too, who gets up with two wounds and no dignity.
 *
 * Fields, for the renderer and audio: `moving`/`travelled` drive the wheels, `facing` the hull,
 * `vehicle.aboard` the men inside. `peeking` is never set: a vehicle has no weapon.
 */

import { type Ctx, isAlive } from '../../ctx';
import { damageEnemy, woundRanger } from '../../damage';
import { rangerOnFoot, within } from '../../hazards/terrain';
import { atan2 } from '../../math';
import type { Wreck } from '../../statics';
import { VEHICLES } from '../../tuning';
import { DT, type Enemy, TICK_RATE, type VehicleState } from '../../types';
import { turnToward } from '../movement';
import { isAutomated, restoreArmour } from './kinds';
import { dismountAll } from './vehicleCrew';
import { distanceToSpine, hullBlocked, hullSpine, wreckHeightOf, wreckRadiusOf } from './vehicleHull';

const SMALL = 1e-4;

/** The waypoint it is making for, or null when its run has no road left in it. */
function waypoint(v: VehicleState): readonly [number, number] | null {
  return v.route.points.points[v.node] ?? null;
}

/** Reached one: on to the next. With `loop` it turns round at each end; otherwise the run is over and it goes. */
function advance(v: VehicleState): void {
  const last = v.route.points.points.length - 1;
  if (last < 0) return;
  if (v.reversing) {
    v.node--;
    if (v.node >= 0) return;
    // Back where it came from: a beat turns round again, a one-way run is over (the courier who turned back).
    if (!v.route.loop) {
      v.gone = true;
      v.node = 0;
      return;
    }
    v.reversing = false;
    v.node = Math.min(1, last);
    return;
  }
  v.node++;
  if (v.node <= last) return;
  if (!v.route.loop) {
    v.gone = true;
    v.node = last;
    return;
  }
  v.reversing = true;
  v.node = Math.max(0, last - 1);
}

/** Metres covered this tick; 0 when it has arrived at a waypoint, or when something solid is in the way. */
function drive(ctx: Ctx, e: Enemy, v: VehicleState): number {
  const node = waypoint(v);
  if (!node) return 0;
  const dx = node[0] - e.x;
  const dz = node[1] - e.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d <= VEHICLES.nodeReach) {
    advance(v);
    return 0;
  }
  turnToward(e, atan2(dx, dz), VEHICLES.turnRate);
  const speed = v.route.speed > 0 ? v.route.speed : VEHICLES.speed;
  const step = Math.min(d, speed * DT);
  const def = ctx.statics.def;
  const r = VEHICLES.radius;
  const nx = Math.min(def.sizeX - r, Math.max(r, e.x + (dx / d) * step));
  const nz = Math.min(def.sizeZ - r, Math.max(r, e.z + (dz / d) * step));
  // Nothing steers it round an obstacle: a wall, a hut or another vehicle's wreck brings it to a stand -
  // and it is the NOSE that runs into it, which for a locomotive is five metres ahead of its middle.
  if (hullBlocked(ctx.statics.colliders, e.kind, nx, nz, e.facing)) return 0;
  const gone = Math.sqrt((nx - e.x) * (nx - e.x) + (nz - e.z) * (nz - e.z));
  e.x = nx;
  e.z = nz;
  e.travelled += gone;
  return gone;
}

/** Anyone under the wheels, anywhere along the hull. Men of its own party the driver can see. */
function runOver(ctx: Ctx, e: Enemy, speed: number): void {
  const w = ctx.world;
  const hull = hullSpine(e.kind, e.x, e.z, e.facing);
  for (const o of w.enemies) {
    if (o.id === e.id || !isAlive(o) || isAutomated(o.kind)) continue;
    // Its own party - the escort it came with, the section it has just put down - the driver can see.
    if (o.wokenBy !== undefined && (o.wokenBy === e.wokenBy || o.wokenBy === `aboard-${e.id}`)) continue;
    if (distanceToSpine(hull, o.x, o.z) > VEHICLES.runOverRadius) continue;
    damageEnemy(ctx, o, 99, false);
    o.found = true;
  }
  const r = w.ranger;
  if (e.fireCooldown > 0 || speed < VEHICLES.runOverSpeed || !rangerOnFoot(w)) return;
  if (distanceToSpine(hull, r.x, r.z) > VEHICLES.runOverRadius) return;
  e.fireCooldown = VEHICLES.runOverEveryTicks;
  r.flatTicks = VEHICLES.knockedFlatTicks;
  r.stance = 'crawl';
  r.running = false;
  r.staggerTicks = VEHICLES.knockedFlatTicks;
  r.action = 'none';
  r.actionTicks = 0;
  woundRanger(ctx, VEHICLES.runOverWounds, 'vehicle');
}

/** A car sent for one man does not pull away without him (mission 29: the general hurried to his staff car). */
function waitingForTarget(ctx: Ctx, e: Enemy): boolean {
  const o = ctx.world.objective;
  if (!o.targetFleeing || o.targetAboardId !== -1 || o.targetEnemyId === -1) return false;
  const man = ctx.world.enemies.find((m) => m.id === o.targetEnemyId);
  return man !== undefined && isAlive(man) && within(man.x, man.z, e.x, e.z, VEHICLES.waitRadius);
}

export function stepVehicle(ctx: Ctx, e: Enemy): void {
  const v = e.vehicle;
  restoreArmour(e);
  e.moving = false;
  if (!v || v.gone) return;
  const started = ctx.world.objective.groundTicks >= (v.route.startAtSeconds ?? 0) * TICK_RATE;
  if (!started || waitingForTarget(ctx, e)) {
    v.stalledTicks = 0;
    return;
  }

  const gone = drive(ctx, e, v);
  e.moving = gone > SMALL;
  if (e.moving) runOver(ctx, e, gone * TICK_RATE);

  // Stopped dead with men inside: they get down and go on on foot. A vehicle with no route at all is parked,
  // not stopped - it never had anywhere to be.
  if (v.route.points.points.length > 1 && !v.gone) {
    if (gone > SMALL) v.stalledTicks = 0;
    else v.stalledTicks++;
    if (v.stalledTicks >= VEHICLES.stalledTicks && v.aboard > 0) dismountAll(ctx, e, v);
  }
  const at = v.dismountAt;
  if (at && v.aboard > 0 && within(e.x, e.z, at.x, at.z, VEHICLES.dismountReach)) dismountAll(ctx, e, v);
}

/**
 * Wrecks, for the navigation grid and the movement resolver: a hulk in the road nobody walks through, as wide
 * and as TALL as the wreck MODEL is - a staff car is half the lorry it was measured against
 * (`wreckRadiusOf`), and low enough to shoot over (`wreckHeightOf`).
 */
export function vehicleWrecks(enemies: readonly Enemy[]): Wreck[] {
  const out: Wreck[] = [];
  for (const e of enemies) {
    if (e.vehicle === undefined || e.state !== 'dead') continue;
    out.push({
      id: e.id, x: e.x, z: e.z, shape: 'circle', r: wreckRadiusOf(e.kind), h: wreckHeightOf(e.kind),
    });
  }
  return out;
}
